import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, Space, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { useCreateLocationMutation, useDeleteLocationMutation, useGetLocationByIdQuery, useGetLocationsQuery, useUpdateLocationMutation } from "../../api/location.api";
import { ListLocationDto, LocationDto, MutableLocationDto } from "../../models/location.models";
import { LocationList } from "../../Components/Locations/LocationList";
import { LocationDetailView } from "../../Components/Locations/LocationDetailView";
import LocationEditView from "../../Components/Locations/LocationEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const LocationsPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ locationId?: string }>();
	const isEditing = useMatch("/locations/:locationId/edit") !== null;
	const isNew = params.locationId === "new";
	const locationIdFromParams = params.locationId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const { data: locations, isPending: isLoadingLocations } = useGetLocationsQuery();
	const [selectedLocation, setSelectedLocation] = useState<ListLocationDto | null>(null);
	const { data: location, isLoading } = useGetLocationByIdQuery(selectedLocation?.LocationId || locationIdFromParams || null);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedLocationId, setRequestedLocationId] = useState<string | null>(null);

	const { mutate: createLocation, isPending: isCreating } = useCreateLocationMutation();
	const { mutate: updateLocation, isPending: isUpdating } = useUpdateLocationMutation();
	const { mutate: deleteLocation } = useDeleteLocationMutation();

	const isSaving = isCreating || isUpdating;

	useEffect(() => {
		if (locationIdFromParams) {
			const location = locations?.find((loc: ListLocationDto) => loc.LocationId === locationIdFromParams);
			if (location) {
				setSelectedLocation?.(location);
				if (location) {
					setSelectedLocation?.(location);
					if (isMobile) {
						openDetailViewDrawer();
					}
				}
			}
		} else if (locations && locations.length > 0) {
			setSelectedLocation?.(locations[0]);
		}
	}, [locationIdFromParams, locations, isMobile]);

	useEffect(() => {
		if (!locationIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleEdit = () => {
		navigate(`/locations/${location?.LocationId}/edit`);
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedLocation(null);
		navigate(`/locations/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleLocationSelect = (location: ListLocationDto | null) => {
		if (!location)
			return;

		setRequestedLocationId(location.LocationId);

		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}

		setSelectedLocation(location);
		navigate(`/locations/${location.LocationId}`);
		// setIsEditing(false);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (values: MutableLocationDto) => {
		if (isNew) {
			createLocation(values, {
				onSuccess: (response) => {
					setIsChanged(false);
					navigate(`/locations/${response.data.LocationId}`, { replace: true });
				}
			});
		} else {
			updateLocation(values, {
				onSuccess: () => {
					
					setIsChanged(false);
					// setIsEditing(false);
					// history.replace(`/locations/${location?.LocationId}`);
					navigate(-1);
				}
			});
		}
	}

	const handleDelete = () => {
		if (!location) return;

		closeDeleteModal();
		deleteLocation(location, {
			onSuccess: () => {
				setSelectedLocation(null);
				navigate(`/locations`, { replace: true });
			}
		});
	}

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			// setSelectedWorkOrder(null);
			// setIsEditing(false);
			// history.replace(`/workorders/${workOrder?.WorkOrderId}`);
			setIsChanged(false);

			// if (selectedWorkOrder == null)	
			navigate(-1);
			// history.push(`/workorders`);

			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		// setIsEditing(false);
		setIsChanged(false);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}
		// if (selectedWorkOrder == null){
			// history.push(`/workorders`);
			// history.goBack();
			// return;
		// }

		if (requestedLocationId && requestedLocationId !== location?.LocationId) {
			navigate(`/locations/${requestedLocationId}`);
			// history.goBack();
			setRequestedLocationId(null);
		} else {
			navigate(-1);
		}
	}

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
		// setPendingClose(false);
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<DeleteLocationModal
			location={location}
			open={deleteModalOpen}
			onDelete={handleDelete}
			onClose={closeDeleteModal}
		/>

		<DiscardChangesModal
			open={discardChangesModalOpened}
			onDiscard={handleDiscardChanges}
			onClose={handleCancelDiscard}
		/>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditing || isNew ? (
				<LocationEditView location={!isNew ? location : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
			) : (
				<LocationDetailView location={location} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}	
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Locations</Title>
			<RoleTemplate role={AzureADRoles.Admin}>
				<Button onClick={handleNew} radius="xl" leftSection={<IconPlus />}>New Location</Button>
			</RoleTemplate>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!locations || locations.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<LocationList locations={locations} isLoading={isLoadingLocations} selectedLocation={selectedLocation} setSelectedLocation={handleLocationSelect} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditing || isNew ? (
					<LocationEditView location={!isNew ? location : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				) : locations && locations?.length > 0 ? (
					<LocationDetailView location={location} isLoading={isLoading} onEdit={handleEdit} onDelete={openDeleteModal} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>
}

export default LocationsPage

const DiscardChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you’ll lose unsaved changes to this location.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteLocationModal = ({ location, open, onDelete, onClose }: { location?: LocationDto, open: boolean, onDelete: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete Location</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete this location?</Text>
				{/* <Text fw="bold">{location?.Title}</Text> */}
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
